import React, { useEffect, useState, useContext, useCallback, useMemo } from 'react';
import Fuse from 'fuse.js';

import Paper from '@material-ui/core/Paper';
import Card from '@material-ui/core/Card';
import CardActionArea from '@material-ui/core/CardActionArea';
import CardMedia from '@material-ui/core/CardMedia';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import TextField from '@material-ui/core/TextField';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import OndemandVideoIcon from '@material-ui/icons/OndemandVideo';

import { AppContext } from '../../Context/AppContextProvide';
import {
  getHelpReviews,
  approveHelpVideo,
  requestHelpVideoChanges,
  setHelpVideoActive,
  deleteHelpVideo,
} from '../../Actions/';

const statusColor = (s) =>
  s === 'approved' ? '#2e7d32' : s === 'changes_requested' ? '#c62828' : '#f57c00';
const statusRank = (s) => (s === 'approved' ? 2 : s === 'changes_requested' ? 0 : 1);

// The review API returns absolute thumbnail/video URLs (always staging).
const thumbFor = (v) => v.thumbnail || undefined;

const HelpVideoReview = () => {
  const { user } = useContext(AppContext);
  const by = user?.user_name || user?.user_id || 'unknown';

  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);
  const [playing, setPlaying] = useState(null); // video being watched
  const [dialog, setDialog] = useState(null); // { key, title } for request-changes
  const [note, setNote] = useState('');
  const [query, setQuery] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null); // { key, title }

  const load = useCallback(() => {
    setLoading(true);
    getHelpReviews()
      .then((r) => {
        const list = (r.data?.videos || []).slice().sort(
          (a, b) => statusRank(a.status) - statusRank(b.status) || a.title.localeCompare(b.title),
        );
        setVideos(list);
      })
      .catch(() => setVideos([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const approve = (key) => {
    setBusyKey(key);
    approveHelpVideo(key, by).then(load).finally(() => setBusyKey(null));
  };

  const submitChanges = () => {
    const { key } = dialog;
    setBusyKey(key);
    requestHelpVideoChanges(key, by, note)
      .then(() => {
        setDialog(null);
        setNote('');
        load();
      })
      .finally(() => setBusyKey(null));
  };

  const toggleActive = (key, active) => {
    setBusyKey(key);
    setHelpVideoActive(key, active).then(load).finally(() => setBusyKey(null));
  };

  const doDelete = () => {
    const { key } = confirmDelete;
    setBusyKey(key);
    deleteHelpVideo(key)
      .then(() => {
        setConfirmDelete(null);
        load();
      })
      .finally(() => setBusyKey(null));
  };

  const results = useMemo(() => {
    if (!query.trim()) return videos;
    const fuse = new Fuse(videos, {
      keys: ['title', 'description', 'category', 'keywords'],
      threshold: 0.3,
      ignoreLocation: true,
      minMatchCharLength: 2,
    });
    return fuse.search(query).map((r) => r.item);
  }, [videos, query]);

  const Thumb = ({ v }) => {
    const [err, setErr] = useState(false);
    const t = thumbFor(v);
    const style = { width: 160, height: 90, flexShrink: 0, objectFit: 'cover' };
    return t && !err ? (
      <CardMedia component="img" image={t} title={v.title} onError={() => setErr(true)} style={style} />
    ) : (
      <div style={{ ...style, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#eceff1' }}>
        <OndemandVideoIcon style={{ fontSize: 40, color: '#90a4ae' }} />
      </div>
    );
  };

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      <Typography variant="h4" gutterBottom>
        Help Video Review
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Watch a video, then approve it to publish to customers on the next release, or request
        changes. Pending items are listed first.
      </Typography>

      <TextField
        fullWidth
        variant="outlined"
        placeholder="Search videos…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        style={{ marginTop: 12, marginBottom: 8 }}
      />

      {loading ? (
        <CircularProgress style={{ marginTop: 24 }} />
      ) : (
        <div style={{ marginTop: 16 }}>
          {results.length === 0 && (
            <Typography variant="body2" color="textSecondary" style={{ padding: 12 }}>
              No videos match “{query}”.
            </Typography>
          )}
          {results.map((v) => (
            <Card key={v.key} elevation={2} style={{ display: 'flex', alignItems: 'stretch', marginBottom: 10, opacity: v.active === false ? 0.6 : 1 }}>
              <CardActionArea
                onClick={() => setPlaying(v)}
                style={{ display: 'flex', flex: 1, alignItems: 'stretch', justifyContent: 'flex-start' }}
              >
                <Thumb v={v} />
                <CardContent style={{ flex: 1, paddingTop: 8, paddingBottom: 8 }}>
                  <Typography variant="subtitle1" component="div" style={{ fontWeight: 600 }}>
                    {v.title}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {v.description}
                  </Typography>
                  <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <Chip label={v.status} size="small" style={{ backgroundColor: statusColor(v.status), color: '#fff' }} />
                    <Chip label={v.host === 'youtube' ? 'YouTube' : 'Native'} size="small" variant="outlined" />
                    {v.active === false && (
                      <Chip label="disabled" size="small" style={{ backgroundColor: '#9e9e9e', color: '#fff' }} />
                    )}
                    <Typography variant="caption" color="textSecondary">
                      {v.category}
                      {v.generated ? ' · generated' : ''}
                      {v.reviewed_by ? ` · by ${v.reviewed_by}` : ''}
                      {v.feedback && v.feedback.length ? ` · ${v.feedback.length} note${v.feedback.length > 1 ? 's' : ''}` : ''}
                    </Typography>
                    {v.issue_url && (
                      <Typography variant="caption">
                        ·{' '}
                        <a
                          href={v.issue_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          style={{ color: '#1565c0' }}
                        >
                          issue #{v.issue_number}
                        </a>
                      </Typography>
                    )}
                  </div>
                </CardContent>
              </CardActionArea>
              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 8, padding: '8px 12px', flexShrink: 0 }}>
                <Button
                  size="small"
                  variant="contained"
                  disabled={busyKey === v.key || v.status === 'approved'}
                  onClick={() => approve(v.key)}
                  style={{ backgroundColor: v.status === 'approved' ? undefined : '#2e7d32', color: v.status === 'approved' ? undefined : '#fff' }}
                >
                  Approve
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={busyKey === v.key}
                  onClick={() => { setDialog({ key: v.key, title: v.title }); setNote(''); }}
                >
                  Request changes
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={busyKey === v.key}
                  onClick={() => toggleActive(v.key, v.active === false)}
                >
                  {v.active === false ? 'Enable' : 'Disable'}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={busyKey === v.key}
                  onClick={() => setConfirmDelete({ key: v.key, title: v.title })}
                  style={{ color: '#c62828', borderColor: '#c62828' }}
                >
                  Delete
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Player */}
      <Dialog open={!!playing} onClose={() => setPlaying(null)} fullWidth maxWidth="lg">
        <DialogTitle disableTypography>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h5">{playing ? playing.title : ''}</Typography>
            <IconButton aria-label="close" onClick={() => setPlaying(null)} size="small">
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent>
          {playing && playing.host === 's3' ? (
            <div className="video-responsive">
              <video controls autoPlay width="100%" src={playing.video_url} />
            </div>
          ) : (
            playing && (
              <div className="video-responsive">
                <iframe
                  width="100%"
                  height="500"
                  src={`https://www.youtube.com/embed/${playing.video_id}`}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title={playing.title}
                />
              </div>
            )
          )}
        </DialogContent>
      </Dialog>

      {/* Request changes */}
      <Dialog open={!!dialog} onClose={() => setDialog(null)} fullWidth maxWidth="sm">
        <DialogTitle>Request changes{dialog ? `: ${dialog.title}` : ''}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            variant="outlined"
            placeholder="What needs to change? (narration wording, a missed step, pronunciation…)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Cancel</Button>
          <Button variant="contained" color="primary" disabled={!note.trim()} onClick={submitChanges}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete video?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This removes “{confirmDelete ? confirmDelete.title : ''}” from the catalog. If you might
            bring it back later, use <strong>Disable</strong> instead.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button variant="contained" style={{ backgroundColor: '#c62828', color: '#fff' }} onClick={doDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default HelpVideoReview;
